"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@multi-tenant-cms/ui/components/button";
import { Checkbox } from "@multi-tenant-cms/ui/components/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@multi-tenant-cms/ui/components/dialog";
import { Label } from "@multi-tenant-cms/ui/components/label";
import { cn } from "@multi-tenant-cms/ui/lib/utils";
import { CookieIcon, Settings2Icon } from "lucide-react";
import { useEffect, useState } from "react";
import type { CookieConsentCategorySelection, CookieConsentConfig, OptionalCookieConsentCategoryKey } from "../../../types/src/seo";
const OPTIONAL_CATEGORIES: readonly OptionalCookieConsentCategoryKey[] = ["preferences", "analytics", "marketing"];
type CookieConsentSurfaceProps = {
  config: CookieConsentConfig;
  onAcceptAll?: () => void;
  onRejectAll?: () => void;
  onSavePreferences?: (nextSelection: CookieConsentCategorySelection) => void;
  previewMode?: boolean;
  selection: CookieConsentCategorySelection;
  showBanner: boolean;
};
function bannerPlacementClass(position: CookieConsentConfig["banner_position"], previewMode: boolean) {
  const base = previewMode ? "absolute inset-x-4 bottom-4" : "fixed z-50 inset-x-4 bottom-4";
  if (position === "bottom-left") {
    return cn(base, "mx-0 max-w-md md:right-auto md:left-4");
  }
  if (position === "bottom-center") {
    return cn(base, "mx-auto max-w-2xl");
  }
  return cn(base, "mx-0 max-w-md md:right-4 md:left-auto");
}
function manageButtonPlacementClass(position: CookieConsentConfig["banner_position"], previewMode: boolean) {
  const base = previewMode ? "absolute bottom-4" : "fixed bottom-4 z-40";
  if (position === "bottom-left") {
    return cn(base, "right-4");
  }
  if (position === "bottom-center") {
    return cn(base, "right-4");
  }
  return cn(base, "left-4");
}
export function CookieConsentSurface(t0) {
  const $ = _c(108);
  const {
    config,
    onAcceptAll,
    onRejectAll,
    onSavePreferences,
    previewMode: t1,
    selection,
    showBanner
  } = t0;
  const previewMode = t1 === undefined ? false : t1;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draftSelection, setDraftSelection] = useState(selection);
  let t2;
  let t3;
  if ($[0] !== selection) {
    t2 = () => {
      setDraftSelection(selection);
    };
    t3 = [selection];
    $[0] = selection;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  if (!config.enabled) {
    return null;
  }
  let T0;
  let T1;
  let t10;
  let t11;
  let t12;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[3] !== config.accept_all_label || $[4] !== config.banner_description || $[5] !== config.banner_position || $[6] !== config.banner_title || $[7] !== config.categories || $[8] !== config.cookie_policy_url || $[9] !== config.customize_label || $[10] !== config.manage_preferences_label || $[11] !== config.preferences_description || $[12] !== config.preferences_title || $[13] !== config.privacy_policy_url || $[14] !== config.reject_all_label || $[15] !== dialogOpen || $[16] !== draftSelection || $[17] !== onAcceptAll || $[18] !== onRejectAll || $[19] !== previewMode || $[20] !== showBanner) {
    let t13;
    if ($[32] !== config.categories) {
      t13 = category => config.categories[category].enabled;
      $[32] = config.categories;
      $[33] = t13;
    } else {
      t13 = $[33];
    }
    const categoryRows = OPTIONAL_CATEGORIES.filter(t13);
    const shouldShowManageButton = !showBanner;
    const hasPolicyLinks = Boolean(config.privacy_policy_url || config.cookie_policy_url);
    if ($[34] !== config.banner_position || $[35] !== config.manage_preferences_label || $[36] !== previewMode || $[37] !== shouldShowManageButton) {
      t11 = shouldShowManageButton ? <div className={manageButtonPlacementClass(config.banner_position, previewMode)}><Button onClick={() => setDialogOpen(true)} size="sm" type="button" variant="outline"><Settings2Icon className="size-4" />{config.manage_preferences_label}</Button></div> : null;
      $[34] = config.banner_position;
      $[35] = config.manage_preferences_label;
      $[36] = previewMode;
      $[37] = shouldShowManageButton;
      $[38] = t11;
    } else {
      t11 = $[38];
    }
    if ($[39] !== config.accept_all_label || $[40] !== config.banner_description || $[41] !== config.banner_position || $[42] !== config.banner_title || $[43] !== config.cookie_policy_url || $[44] !== config.customize_label || $[45] !== config.privacy_policy_url || $[46] !== config.reject_all_label || $[47] !== hasPolicyLinks || $[48] !== onAcceptAll || $[49] !== onRejectAll || $[50] !== previewMode || $[51] !== showBanner) {
      t12 = showBanner ? <div className={bannerPlacementClass(config.banner_position, previewMode)}><div className="rounded-xl border border-border bg-background/95 p-4 shadow-2xl backdrop-blur"><div className="flex items-start gap-3"><div className="mt-0.5 rounded-full bg-primary/10 p-2 text-primary"><CookieIcon className="size-4" /></div><div className="min-w-0 flex-1 space-y-2"><div><p className="font-semibold text-foreground text-sm">{config.banner_title}</p><p className="mt-1 text-muted-foreground text-sm">{config.banner_description}</p></div>{hasPolicyLinks ? <div className="flex flex-wrap gap-3 text-sm">{config.privacy_policy_url ? <a className="text-primary underline underline-offset-4" href={config.privacy_policy_url} rel="noreferrer" target="_blank">Privacy policy</a> : null}{config.cookie_policy_url ? <a className="text-primary underline underline-offset-4" href={config.cookie_policy_url} rel="noreferrer" target="_blank">Cookie policy</a> : null}</div> : null}<div className="flex flex-wrap gap-2"><Button onClick={onRejectAll} size="sm" type="button" variant="outline">{config.reject_all_label}</Button><Button onClick={() => setDialogOpen(true)} size="sm" type="button" variant="secondary">{config.customize_label}</Button><Button onClick={onAcceptAll} size="sm" type="button">{config.accept_all_label}</Button></div></div></div></div></div> : null;
      $[39] = config.accept_all_label;
      $[40] = config.banner_description;
      $[41] = config.banner_position;
      $[42] = config.banner_title;
      $[43] = config.cookie_policy_url;
      $[44] = config.customize_label;
      $[45] = config.privacy_policy_url;
      $[46] = config.reject_all_label;
      $[47] = hasPolicyLinks;
      $[48] = onAcceptAll;
      $[49] = onRejectAll;
      $[50] = previewMode;
      $[51] = showBanner;
      $[52] = t12;
    } else {
      t12 = $[52];
    }
    T1 = Dialog;
    t9 = setDialogOpen;
    t10 = dialogOpen;
    T0 = DialogContent;
    t7 = "max-w-2xl";
    let t14;
    if ($[53] !== config.preferences_title) {
      t14 = <DialogTitle>{config.preferences_title}</DialogTitle>;
      $[53] = config.preferences_title;
      $[54] = t14;
    } else {
      t14 = $[54];
    }
    let t15;
    if ($[55] !== config.preferences_description) {
      t15 = <DialogDescription>{config.preferences_description}</DialogDescription>;
      $[55] = config.preferences_description;
      $[56] = t15;
    } else {
      t15 = $[56];
    }
    if ($[57] !== t14 || $[58] !== t15) {
      t8 = <DialogHeader>{t14}{t15}</DialogHeader>;
      $[57] = t14;
      $[58] = t15;
      $[59] = t8;
    } else {
      t8 = $[59];
    }
    t4 = "space-y-4";
    let t16;
    if ($[60] !== config.categories.essential.label) {
      t16 = <p className="font-medium text-sm">{config.categories.essential.label}</p>;
      $[60] = config.categories.essential.label;
      $[61] = t16;
    } else {
      t16 = $[61];
    }
    let t17;
    if ($[62] !== config.categories.essential.description) {
      t17 = <p className="text-muted-foreground text-sm">{config.categories.essential.description}</p>;
      $[62] = config.categories.essential.description;
      $[63] = t17;
    } else {
      t17 = $[63];
    }
    let t18;
    if ($[64] !== t16 || $[65] !== t17) {
      t18 = <div className="space-y-1">{t16}{t17}</div>;
      $[64] = t16;
      $[65] = t17;
      $[66] = t18;
    } else {
      t18 = $[66];
    }
    let t19;
    if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <div className="flex items-center gap-2"><Checkbox checked={true} disabled={true} /><span className="text-muted-foreground text-sm">Always on</span></div>;
      $[67] = t19;
    } else {
      t19 = $[67];
    }
    if ($[68] !== t18) {
      t5 = <div className="rounded-lg border border-border bg-muted/30 p-4"><div className="flex items-start justify-between gap-4">{t18}{t19}</div></div>;
      $[68] = t18;
      $[69] = t5;
    } else {
      t5 = $[69];
    }
    let t20;
    if ($[70] !== config.categories || $[71] !== draftSelection) {
      t20 = category_0 => <div className="rounded-lg border border-border p-4" key={category_0}><div className="flex items-start justify-between gap-4"><div className="space-y-1"><Label className="font-medium text-sm" htmlFor={`cookie-category-${category_0}`}>{config.categories[category_0].label}</Label><p className="text-muted-foreground text-sm">{config.categories[category_0].description}</p></div><Checkbox checked={draftSelection[category_0]} id={`cookie-category-${category_0}`} onCheckedChange={checked => setDraftSelection(current => ({
            ...current,
            [category_0]: checked === true
          }))} /></div></div>;
      $[70] = config.categories;
      $[71] = draftSelection;
      $[72] = t20;
    } else {
      t20 = $[72];
    }
    t6 = categoryRows.map(t20);
    $[3] = config.accept_all_label;
    $[4] = config.banner_description;
    $[5] = config.banner_position;
    $[6] = config.banner_title;
    $[7] = config.categories;
    $[8] = config.cookie_policy_url;
    $[9] = config.customize_label;
    $[10] = config.manage_preferences_label;
    $[11] = config.preferences_description;
    $[12] = config.preferences_title;
    $[13] = config.privacy_policy_url;
    $[14] = config.reject_all_label;
    $[15] = dialogOpen;
    $[16] = draftSelection;
    $[17] = onAcceptAll;
    $[18] = onRejectAll;
    $[19] = previewMode;
    $[20] = showBanner;
    $[21] = T0;
    $[22] = T1;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
  } else {
    T0 = $[21];
    T1 = $[22];
    t10 = $[23];
    t11 = $[24];
    t12 = $[25];
    t4 = $[26];
    t5 = $[27];
    t6 = $[28];
    t7 = $[29];
    t8 = $[30];
    t9 = $[31];
  }
  let t13;
  if ($[73] !== t4 || $[74] !== t5 || $[75] !== t6) {
    t13 = <div className={t4}>{t5}{t6}</div>;
    $[73] = t4;
    $[74] = t5;
    $[75] = t6;
    $[76] = t13;
  } else {
    t13 = $[76];
  }
  let t14;
  if ($[77] !== config.privacy_policy_url) {
    t14 = config.privacy_policy_url ? <a className="text-primary underline underline-offset-4" href={config.privacy_policy_url} rel="noreferrer" target="_blank">Privacy policy</a> : null;
    $[77] = config.privacy_policy_url;
    $[78] = t14;
  } else {
    t14 = $[78];
  }
  let t15;
  if ($[79] !== config.cookie_policy_url) {
    t15 = config.cookie_policy_url ? <a className="text-primary underline underline-offset-4" href={config.cookie_policy_url} rel="noreferrer" target="_blank">Cookie policy</a> : null;
    $[79] = config.cookie_policy_url;
    $[80] = t15;
  } else {
    t15 = $[80];
  }
  let t16;
  if ($[81] !== t14 || $[82] !== t15) {
    t16 = <div className="flex flex-wrap gap-3 text-sm">{t14}{t15}</div>;
    $[81] = t14;
    $[82] = t15;
    $[83] = t16;
  } else {
    t16 = $[83];
  }
  let t17;
  if ($[84] !== draftSelection || $[85] !== onSavePreferences) {
    t17 = () => {
      onSavePreferences?.({
        ...draftSelection,
        essential: true
      });
      setDialogOpen(false);
    };
    $[84] = draftSelection;
    $[85] = onSavePreferences;
    $[86] = t17;
  } else {
    t17 = $[86];
  }
  let t18;
  if ($[87] !== config.save_preferences_label || $[88] !== t17) {
    t18 = <Button onClick={t17} type="button">{config.save_preferences_label}</Button>;
    $[87] = config.save_preferences_label;
    $[88] = t17;
    $[89] = t18;
  } else {
    t18 = $[89];
  }
  let t19;
  if ($[90] !== t16 || $[91] !== t18) {
    t19 = <DialogFooter className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">{t16}{t18}</DialogFooter>;
    $[90] = t16;
    $[91] = t18;
    $[92] = t19;
  } else {
    t19 = $[92];
  }
  let t20;
  if ($[93] !== T0 || $[94] !== t13 || $[95] !== t19 || $[96] !== t7 || $[97] !== t8) {
    t20 = <T0 className={t7}>{t8}{t13}{t19}</T0>;
    $[93] = T0;
    $[94] = t13;
    $[95] = t19;
    $[96] = t7;
    $[97] = t8;
    $[98] = t20;
  } else {
    t20 = $[98];
  }
  let t21;
  if ($[99] !== T1 || $[100] !== t10 || $[101] !== t20 || $[102] !== t9) {
    t21 = <T1 onOpenChange={t9} open={t10}>{t20}</T1>;
    $[99] = T1;
    $[100] = t10;
    $[101] = t20;
    $[102] = t9;
    $[103] = t21;
  } else {
    t21 = $[103];
  }
  let t22;
  if ($[104] !== t11 || $[105] !== t12 || $[106] !== t21) {
    t22 = <>{t11}{t12}{t21}</>;
    $[104] = t11;
    $[105] = t12;
    $[106] = t21;
    $[107] = t22;
  } else {
    t22 = $[107];
  }
  return t22;
}